import { RefObject, useRef, useState } from 'react';
import {
  Avatar,
  Button,
  Combobox,
  Flex,
  Group,
  Highlight,
  Loader,
  ScrollArea,
  Text,
  TextInput,
  Tooltip,
  useCombobox,
} from '@mantine/core';
import { IconCirclePlus, IconPlus } from '@tabler/icons-react';
import { useDebouncedValue } from '@mantine/hooks';
import { useSearchPatients } from 'api/resource';
import { Patient, SearchPatientResult } from 'interfaces';
import { useActiveForm } from 'pages/claims/components';
import { useCurrentSiteStore } from 'store/SiteStore';
import { formatPatientName } from '@billing/validation';
import { getValueByPath } from 'utils/getValueByPath';

export const PatientSearchComponent = ({
  setIsNewPatient,
  patientPath,
  patientIDPath,
  healthcardPath,
  healthcardIDPath,
  payorPath,
  isNewPatientPath,
  cardRef,
}: {
  setIsNewPatient: (value: boolean) => void;
  patientPath: string;
  patientIDPath: string;
  healthcardPath: string;
  healthcardIDPath: string;
  payorPath: string;
  isNewPatientPath: string;
  cardRef: RefObject<HTMLDivElement | null>;
}) => {
  const { form } = useActiveForm();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue, 500);
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
    },
  });
  const { organizationID } = useCurrentSiteStore();

  const { data: searchResults = [], isLoading } = useSearchPatients({
    searchValue: debouncedSearch,
    organizationID,
  });

  const handleSelectValue = (val: string | SearchPatientResult) => {
    if (typeof val === 'string' && val.startsWith('$create:')) {
      const name = val.replace('$create:', '').trim();
      setIsNewPatient(true);
      setSearchValue(`${name} (New Patient)`);
      form.setFieldValue(isNewPatientPath, true);
      const isNumber = /^\d+$/.test(name);
      if (!isNumber) {
        const splitName = name.replace(' ', ',').split(',');
        const newPatient: Partial<Patient> = {
          firstName:
            splitName[0]?.charAt(0).toUpperCase() + splitName[0]?.slice(1) ||
            '',
          lastName:
            splitName[1]?.charAt(0).toUpperCase() + splitName[1]?.slice(1) ||
            '',
        };
        form.setFieldValue(patientPath, newPatient);
      } else {
        form.setFieldValue(patientPath, { firstName: '', lastName: '' });
      }
      form.setFieldValue(healthcardPath, null);
      form.setFieldValue(healthcardIDPath, null);
      requestAnimationFrame(() => cardRef.current?.focus());
      return;
    } else if (typeof val !== 'string') {
      const payorProvince = getValueByPath(
        form.getValues(),
        payorPath
      )?.province?.toUpperCase();
      const matchingHealthcard = val.healthcards?.find(
        (healthcard) =>
          Boolean(payorProvince) &&
          healthcard?.province?.toUpperCase() === payorProvince
      );
      const selectedHealthcard =
        matchingHealthcard ?? val.healthcards?.[0] ?? null;
      form.setFieldValue(patientIDPath, val.id);
      form.setFieldValue(patientPath, { ...val });
      form.setFieldValue(healthcardPath, selectedHealthcard);
      form.setFieldValue(healthcardIDPath, selectedHealthcard?.id ?? null);
      setSearchValue(`${val.firstName} ${val.lastName}`);
      form.validate();
      requestAnimationFrame(() => cardRef.current?.focus());
    }
  };

  const optionItems = searchResults.map((patient) => {
    const fullName = formatPatientName(patient.firstName, patient.lastName);
    const number = patient.healthcards?.[0]?.number ?? '';
    const registration = patient.healthcards?.[0]?.registration ?? '';
    const dob = patient.dateOfBirth ?? '';
    return (
      <Combobox.Option
        key={patient.id + number}
        value={fullName}
        onClick={(e) => {
          e.stopPropagation();
          handleSelectValue(patient);
          setSearchValue('');
          combobox.closeDropdown();
        }}
      >
        <Flex justify="space-between" align="center" w="100%">
          <Flex align="center" gap="xs">
            <Avatar color="blue" radius="xl" size="md">
              {patient.firstName?.[0]?.toUpperCase()}
              {patient.lastName?.[0]?.toUpperCase()}
            </Avatar>
            <Flex direction="column" w="100%">
              <Text size="sm">
                <Highlight
                  highlight={debouncedSearch ?? ''}
                  highlightStyles={{ backgroundColor: '#ffc069' }}
                >
                  {fullName ?? 'N/A'}
                </Highlight>
              </Text>
              <Group gap={8}>
                <Text size="xs" c="dimmed">
                  <Highlight
                    highlight={debouncedSearch ?? ''}
                    highlightStyles={{ backgroundColor: '#ffc069' }}
                  >
                    {number ? `PHIN: ${number}` : 'N/A'}
                  </Highlight>
                </Text>
                <Text size="xs" c="dimmed">
                  <Highlight
                    highlight={debouncedSearch ?? ''}
                    highlightStyles={{ backgroundColor: '#ffc069' }}
                  >
                    {registration ? `Registration: ${registration}` : 'N/A'}
                  </Highlight>
                </Text>
              </Group>
              <Text size="xs" c="dimmed">
                <Highlight
                  highlight={debouncedSearch ?? ''}
                  highlightStyles={{ backgroundColor: '#ffc069' }}
                >
                  {dob ? `DOB: ${dob}` : 'DOB: N/A'}
                </Highlight>
              </Text>
            </Flex>
          </Flex>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              handleSelectValue(patient);
              setSearchValue('');
              combobox.closeDropdown();
            }}
            variant="outline"
            color="blue"
            size="sm"
            leftSection={<IconCirclePlus size={16} />}
            style={{ cursor: 'pointer' }}
          >
            Add
          </Button>
        </Flex>
      </Combobox.Option>
    );
  });

  const hasExactMatch = searchResults.some((p) => {
    const fullName = `${p.firstName} ${p.lastName}`.toLowerCase();
    const number = p.number?.toLowerCase() ?? '';
    return (
      fullName === debouncedSearch.toLowerCase() ||
      number === debouncedSearch.toLowerCase()
    );
  });

  return (
    <Flex align="stretch" gap="md" w="100%">
      <Combobox
        store={combobox}
        onOptionSubmit={handleSelectValue}
        withinPortal
      >
        <Combobox.DropdownTarget>
          <Flex
            p="xs"
            style={{ cursor: 'text' }}
            onClick={() => combobox.openDropdown()}
            gap="xs"
            align="center"
            w={'100%'}
          >
            <Combobox.EventsTarget>
              <TextInput
                style={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  backgroundColor: 'transparent',
                }}
                type="text"
                ref={inputRef}
                placeholder={isLoading ? 'Searching...' : 'Search patients'}
                data-testid="patient-search-input"
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.currentTarget.value);
                  combobox.updateSelectedOptionIndex();
                }}
                onFocus={() => combobox.openDropdown()}
              />
            </Combobox.EventsTarget>
            <Tooltip label="Add New Patient" position="top">
              <Button
                radius="xl"
                variant="outline"
                size="xs"
                onClick={(e) => {
                  e.stopPropagation();
                  if (searchValue.trim()) {
                    handleSelectValue(`$create:${searchValue.trim()}`);
                  } else {
                    handleSelectValue(`$create:`);
                  }
                }}
              >
                <IconPlus size={16} />
              </Button>
            </Tooltip>
          </Flex>
        </Combobox.DropdownTarget>
        {debouncedSearch?.length > 0 && (
          <Combobox.Dropdown>
            <ScrollArea.Autosize mah={'300px'} type="hover" scrollbarSize={8}>
              <Combobox.Options>
                {isLoading && (
                  <Combobox.Empty>
                    <Loader size={20} />
                  </Combobox.Empty>
                )}
                {!isLoading && optionItems}
                {!isLoading && optionItems?.length === 0 && !hasExactMatch && (
                  <Combobox.Empty>Nothing found</Combobox.Empty>
                )}
                {!isLoading &&
                  searchValue.trim().length > 0 &&
                  !hasExactMatch && (
                    <Combobox.Option value={`$create:${searchValue.trim()}`}>
                      <Group gap="xs" align="center" justify="center">
                        <IconCirclePlus size={14} />
                        <Text>Add “{searchValue.trim()}”</Text>
                      </Group>
                    </Combobox.Option>
                  )}
              </Combobox.Options>
            </ScrollArea.Autosize>
          </Combobox.Dropdown>
        )}
      </Combobox>
    </Flex>
  );
};
